import { Box, Grid, GridItem } from '@chakra-ui/react';
import { yupResolver } from '@hookform/resolvers/yup';
import type { SingleValue } from 'chakra-react-select';
import type React from 'react';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { Controller, useFieldArray, useForm } from 'react-hook-form';
import toast from 'react-hot-toast';
import { RiAddLine, RiDeleteBin6Line } from 'react-icons/ri';
import * as yup from 'yup';
import FaceliftButton from '@/components/Atoms/FaceliftButton';
import FaceliftInput from '@/components/Atoms/FaceliftInput';
import FaceliftSelect from '@/components/Atoms/FaceliftSelect/FaceliftSelect';
import type { SelectItem } from '@/components/Atoms/FaceliftSelect/FaceliftSelect.types';
import FaceliftSuccessProgressModal from '@/components/Molecules/FaceliftSuccessProgressModal';
import Show from '@/components/Molecules/Show/Show';
import { DRAFT_TYPE_LABELS, FORM_TYPES, shippingMethodItems, statusOptions } from '@/constants';
import {
	BgProcessModule,
	useCreateBgProcess,
	useUpdateBgProcess,
} from '@/hooks/service-hooks/bgProcess';
import {
	useGetBulkConsignmentOrders,
} from '@/hooks/service-hooks/consignmentOrders';
import {
	useGetPhysicians,
	useGetShippingLocations,
} from '@/hooks/service-hooks/contactsAccounts';
import {
	useCreateDraft,
} from '@/hooks/service-hooks/drafts';
import { useGetProductsWithSizes } from '@/hooks/service-hooks/products';
import { usePaginatedSelect } from '@/hooks/usePaginatedSelect';
import type { Physician, ShippingLocation } from '@/services/contactsAccounts';
import { useAuthStore, useClinicStore } from '@/store';
import type { BulkOrderData } from '@/types/bulk-order';
import type { ProductWithSizes } from '@/types/product';
import type {
	BulkConsignmentOrderFormData,
	BulkConsignmentOrderFormProps,
} from './types';
import { transformBulkFormToBulkConsignmentOrderRequest } from './utils';

type BulkMutationResult = BulkOrderData | null;

const extractBulkResult = (response: unknown): BulkMutationResult => {
	if (!response || typeof response !== 'object') return null;
	const data = (response as { data?: BulkOrderData }).data;
	return data ?? null;
};

const formatBulkResultMessage = (
	result: BulkMutationResult,
	action: 'create' | 'update',
	successOnly = false,
) => {
	if (!result) return '';

	if (successOnly) {
		const successVerb = action === 'create' ? 'created' : 'updated';
		return `Successfully ${successVerb} ${result.successfulOrders} consignment order(s)!`;
	}

	const verb = action === 'create' ? 'Created' : 'Updated';
	return `${verb} ${result.successfulOrders} of ${result.totalOrders} consignment order(s). ${result.failedOrders} failed.`;
};

const productFieldSchema = yup.object().shape({
	product: yup.mixed<SelectItem>().required('Product is required'),
	productSize: yup.string().required('Size is required'),
	productSizeLabel: yup.string().optional(),
	quantity: yup
		.number()
		.typeError('Quantity must be a number')
		.required('Quantity is required')
		.positive('Quantity must be positive')
		.integer('Quantity must be an integer')
		.default(1),
	orderId: yup.string().optional(),
});

const bulkConsignmentOrderSchema = yup.object().shape({
	clinic: yup.mixed<SelectItem>().required('Clinic is required'),
	physician: yup.mixed<SelectItem>().nullable().optional(),
	consignmentType: yup
		.mixed<SelectItem>()
		.required('Consignment type is required'),
	clinicPO: yup.string().required('Clinic PO is required'),
	shipping: yup.mixed<SelectItem>().required('Shipping method is required'),
	shippingLocation: yup
		.mixed<SelectItem>()
		.required('Shipping location is required'),
	status: yup.string().default('953870000'),
	products: yup
		.array()
		.of(productFieldSchema)
		.min(1, 'At least one product is required'),
	comments: yup
		.string()
		.max(500, 'Comments must be less than 500 characters')
		.optional()
		.default(''),
});

const BulkConsignmentOrderForm: React.FC<BulkConsignmentOrderFormProps> = ({
	mode,
	orderId,
	onSuccess,
	onIsDirtyChange,
	onExitConfirmationDraftActionChange,
	draftId = null,
	existingDraft,
}) => {
	const {
		register,
		handleSubmit,
		formState: { errors, isDirty },
		setValue,
		watch,
		reset,
		control,
		getValues,
	} = useForm<BulkConsignmentOrderFormData>({
		resolver: yupResolver(bulkConsignmentOrderSchema) as any,
		mode: 'onSubmit',
		defaultValues: {
			clinic: null as any,
			physician: null,
			consignmentType: { value: '953870001', label: 'Trunk Stock' },
			clinicPO: '',
			shipping: { value: '953870000', label: 'First Overnight (8:30 am)' },
			shippingLocation: null as any,
			status: '953870000',
			products: [
				{
					product: null as any,
					productSize: '',
					productSizeLabel: '',
					quantity: 1,
					orderId: undefined,
				},
			],
			comments: '',
		},
	});

	const { fields, append, remove } = useFieldArray({
		control,
		name: 'products',
	});
	const user = useAuthStore((state) => state.user?.userInformation);
	const userId = user?._id || null;
	const userName = user ? `${user.firstName} ${user.lastName}` : '';

	const isEditMode = mode === 'edit';
	const isReorderMode = mode === 'reorder';
	const shouldLockFields = isEditMode || isReorderMode;
	
	const [loadedDraftId, setLoadedDraftId] = useState<string | null>(draftId);
	const [isDraftLoaded, setIsDraftLoaded] = useState(false);
	const [hasCheckedForDraft, setHasCheckedForDraft] = useState(false);

	useEffect(() => {
		if (onIsDirtyChange) {
			onIsDirtyChange(!isDirty);
		}
	}, [isDirty]);

	// Draft save mutation - always create new drafts
	const { mutate: createDraftMutation, isLoading: isCreatingDraft } =
		useCreateDraft(() => {
			toast.success('Draft saved successfully');
			if (onSuccess) onSuccess();
		});

	const isSavingDraft = isCreatingDraft;

	// Get clinics from store
	const storeClinics = useClinicStore((state) => state.currentClinics);
	const clinicItems: SelectItem[] = useMemo(() => {
		return storeClinics.map((clinic) => ({
			value: clinic.accountid,
			label: clinic.name,
		}));
	}, [storeClinics]);

	// Consignment type options
	const consignmentTypeItems: SelectItem[] = [
		{ value: '953870001', label: 'Trunk Stock' },
	];

	// Load draft data when available
	// Only load drafts in create mode
	useEffect(() => {
		if (mode !== 'create') return;
		
		if (existingDraft && !isDraftLoaded) {
			reset(existingDraft.formData);
			setLoadedDraftId(existingDraft._id);
			setIsDraftLoaded(true);
			setHasCheckedForDraft(true);
		} else if (draftId || userId) {
			setHasCheckedForDraft(true);
		}
	}, [existingDraft, draftId, userId, reset, mode, isDraftLoaded]);

	// Fetch bulk order data for edit/reorder mode using React Query
	const { data: bulkOrderData } = useGetBulkConsignmentOrders(
		orderId || null,
		(isEditMode || isReorderMode) && !!orderId,
	);

	// Populate form when data is loaded
	useEffect(() => {
		if (bulkOrderData?.data) {
			const bulkData = bulkOrderData.data;
			const relations = bulkData._relations;

			const getShippingMethodLabel = (method: number) => {
				return (
					shippingMethodItems.find((item) => item.value === String(method))
						?.label || 'First Overnight (8:30 am)'
				);
			};

			const getConsignmentTypeLabel = (type?: number) => {
				if (type === 953870001) return 'Trunk Stock';
				return 'Trunk Stock';
			};

			const formData = {
				clinic: relations?.clinic
					? {
						value: relations.clinic.accountid,
						label: relations.clinic.name,
					}
					: (null as any),
				physician: relations?.doctor
					? {
						value: relations.doctor.contactid,
						label: relations.doctor.fullname,
					}
					: null,
				consignmentType: {
					value: String(bulkData.ta_consignmenttype || 953870001),
					label: getConsignmentTypeLabel(bulkData.ta_consignmenttype),
				},
				clinicPO: bulkData.ta_clinicpo || '',
				shipping: {
					value: String(bulkData.ta_fedexshippingmethod || 953870000),
					label: getShippingMethodLabel(bulkData.ta_fedexshippingmethod),
				},
				shippingLocation: relations?.shippingLocation
					? {
						value: relations.shippingLocation.ta_shippinglocationsid,
						label: relations.shippingLocation.ta_addressline
							? `${relations.shippingLocation.ta_name} - ${relations.shippingLocation.ta_addressline}`
							: relations.shippingLocation.ta_name,
					}
					: (null as any),
				status: isReorderMode
					? '953870000'
					: String(bulkData.ta_status || '953870000'),
				products:
					bulkData.products?.map((prod: any, index: number) => {
						const productInfo = relations?.products?.[index];
						const skuMatch = prod['ta_SKU@odata.bind']?.match(
							/ta_products\(([^)]+)\)/,
						);
						const productId = skuMatch ? skuMatch[1] : '';

						return {
							product: productInfo
								? {
									value: productInfo.ta_productid,
									label: productInfo.ta_name,
								}
								: (null as any),
							productSize: productInfo?.ta_productid || productId,
							productSizeLabel: productInfo?.ta_size || '',
							quantity: parseInt(prod.ta_quantity) || 1,
							orderId: prod.orderId,
						};
					}) || [],
				comments: bulkData.ta_comments || '',
			};
			reset(formData as any, { keepDefaultValues: false });
		}
	}, [bulkOrderData, reset, isReorderMode]);

	// React Query mutations with cache invalidation
	const [activeMutation, setActiveMutation] = useState<
		'create' | 'update' | null
	>(null);

	const {
		mutate: createBulkOrder,
		isLoading: isCreating,
		isSuccess: isCreateSuccess,
		isError: isCreateError,
		data: createData,
	} = useCreateBgProcess(() => {
		// Reset form only on successful create (not edit)
		if (!isEditMode) {
			reset(
				{
					clinic: null as any,
					physician: null,
					consignmentType: { value: '953870001', label: 'Trunk Stock' },
					clinicPO: '',
					shipping: { value: '953870000', label: 'First Overnight (8:30 am)' },
					shippingLocation: null as any,
					status: '953870000',
					products: [
						{
							product: null as any,
							productSize: '',
							productSizeLabel: '',
							quantity: 1,
							orderId: undefined,
						},
					],
					comments: '',
				} as any,
				{ keepDefaultValues: false },
			);
		}

		setTimeout(() => {
			if (onSuccess) onSuccess(createData);
		}, 2000);
	});

	const {
		mutate: updateBulkOrder,
		isLoading: isUpdating,
		isSuccess: isUpdateSuccess,
		isError: isUpdateError,
		data: updateData,
	} = useUpdateBgProcess(() => {
		setTimeout(() => {
			onSuccess?.(updateData);
		}, 2000);
	});

	const createResult = useMemo(
		() => extractBulkResult(createData),
		[createData],
	);
	const updateResult = useMemo(
		() => extractBulkResult(updateData),
		[updateData],
	);

	const createPartialFailure =
		isCreateSuccess && createResult?.success === false;
	const updatePartialFailure =
		isUpdateSuccess && updateResult?.success === false;

	const isLoading =
		activeMutation === 'create'
			? isCreating
			: activeMutation === 'update'
				? isUpdating
				: false;
	const hasError =
		activeMutation === 'create'
			? isCreateError || createPartialFailure
			: activeMutation === 'update'
				? isUpdateError || updatePartialFailure
				: false;
	const isMutationSuccess =
		activeMutation === 'create'
			? isCreateSuccess && !createPartialFailure
			: activeMutation === 'update'
				? isUpdateSuccess && !updatePartialFailure
				: false;

	const errorMessage = useMemo(() => {
		if (activeMutation === 'create') {
			if (isCreateError) return 'Unable to create consignment order. Please check your information and try again.';
			if (createPartialFailure) {
				return formatBulkResultMessage(createResult, 'create');
			}
		}
		if (activeMutation === 'update') {
			if (isUpdateError) return 'Unable to update consignment order. Please try again.';
			if (updatePartialFailure) {
				return formatBulkResultMessage(updateResult, 'update');
			}
		}
		return '';
	}, [
		activeMutation,
		createPartialFailure,
		createResult,
		isCreateError,
		isUpdateError,
		updatePartialFailure,
		updateResult,
	]);

	const successMessage = useMemo(() => {
		if (activeMutation === 'create' && isMutationSuccess) {
			return formatBulkResultMessage(createResult, 'create', true);
		}
		if (activeMutation === 'update' && isMutationSuccess) {
			return formatBulkResultMessage(updateResult, 'update', true);
		}
		return '';
	}, [activeMutation, isMutationSuccess, createResult, updateResult]);

	const modalOpen = isLoading || isMutationSuccess || hasError;
	const fallbackTitle =
		activeMutation === 'update'
			? 'Consignment order updated successfully.'
			: 'Consignment order submitted successfully.';

	// Paginated select hooks - only enabled in create mode
	const physicians = usePaginatedSelect({
		queryHook: useGetPhysicians,
		queryParams: {
			clinic_id: (watch('clinic') as SelectItem)?.value || undefined,
			enabled: mode === 'create' && !!watch('clinic'),
		},
		transform: (physician: Physician) => ({
			value: physician.providerId,
			label: physician.providername,
		}),
		errorMessage: 'Unable to load providers',
		resetKey: ((watch('clinic') as SelectItem)?.value as string) || '',
	});

	const shippingLocations = usePaginatedSelect({
		queryHook: useGetShippingLocations,
		queryParams: {
			account_id: (watch('clinic') as SelectItem)?.value || undefined,
			enabled: mode === 'create' && !!watch('clinic'),
		},
		transform: (location: ShippingLocation) => ({
			value: location.ta_shippinglocationsid,
			label: location.ta_addressline
				? `${location.ta_name} - ${location.ta_addressline}`
				: location.ta_name,
		}),
		errorMessage: 'Unable to load shipping locations',
		resetKey: ((watch('clinic') as SelectItem)?.value as string) || '',
	});

	// Products with sizes
	const { data: productsWithSizes, isLoading: productsLoading } =
		useGetProductsWithSizes({
			clinicId: ((watch('clinic') as SelectItem)?.value as string) || '',
			userPort: 'Woundcare+',
			enabled: mode === 'create' && !!watch('clinic'),
		});

	const products = useMemo(() => {
		if (!productsWithSizes?.items) return [];
		return productsWithSizes.items.map((product: ProductWithSizes) => ({
			value: product.productId,
			label: `${product.productName} (${product.qcode})`,
			qcode: product.qcode,
			sizes: product.sizes,
		}));
	}, [productsWithSizes]);

	// Get filtered products for a specific index (excluding already selected product+size combinations)
	const getFilteredProducts = (currentIndex: number): SelectItem[] => {
		if (shouldLockFields) return [];
		
		const selectedCombinations = new Set<string>();
		
		// Collect all selected product+size combinations from other rows
		formValues.products?.forEach((product: any, index: number) => {
			if (index !== currentIndex && product.product?.value && product.productSize) {
				selectedCombinations.add(`${product.product.value}-${product.productSize}`);
			}
		});
		
		// If no combinations are selected yet, return all products
		if (selectedCombinations.size === 0) return products;
		
		// Filter out products that have ALL their sizes already selected
		return products.filter((product: any) => {
			// Check if product has any available sizes left
			const availableSizes = product.sizes?.filter((size: any) => {
				return !selectedCombinations.has(`${product.value}-${size.ta_productid}`);
			});
			
			// Keep product if it has at least one available size
			return availableSizes && availableSizes.length > 0;
		});
	};

	// Get sizes for selected product (excluding already selected sizes for this product)
	const getProductSizes = (productId: string, currentIndex: number): SelectItem[] => {
		const product = productsWithSizes?.items.find(
			(p: ProductWithSizes) => p.productId === productId,
		);
		if (!product?.sizes) return [];
		
		// Collect already selected sizes for this product from other rows
		const selectedSizesForProduct = new Set<string>();
		formValues.products?.forEach((prod: any, index: number) => {
			if (
				index !== currentIndex && 
				prod.product?.value === productId && 
				prod.productSize
			) {
				selectedSizesForProduct.add(prod.productSize);
			}
		});
		
		// Filter out already selected sizes
		return product.sizes
			.filter((size) => !selectedSizesForProduct.has(size.ta_productid))
			.map((size) => ({
				value: size.ta_productid,
				label: size.ta_size,
			}));
	};

	const handleSaveDraft = useCallback(() => {
		const formData = getValues();

		// Generate titleOrReference from clinic name or clinic PO
		const clinicName = (formData.clinic as SelectItem)?.label || '';
		const clinicPO = formData.clinicPO || '';
		const titleOrReference = clinicName || clinicPO || 'Consignment Order Draft';

		const payload = {
			userId: userId || '',
			userName: userName,
			formType: FORM_TYPES.CONSIGNMENT_ORDER,
			draftType: DRAFT_TYPE_LABELS.CONSIGNMENT_ORDER,
			titleOrReference,
			formData,
		};

		// Always create a new draft
		createDraftMutation(payload);
	}, [createDraftMutation, getValues, userId, userName]);

	useEffect(() => {
		if (!onExitConfirmationDraftActionChange) {
			return;
		}

		if (mode !== 'create' || existingDraft) {
			onExitConfirmationDraftActionChange(null);
			return;
		}

		onExitConfirmationDraftActionChange({
			label: 'Save as Draft',
			onClick: handleSaveDraft,
			loading: isSavingDraft,
			disabled: isLoading || isSavingDraft,
		});

		return () => {
			onExitConfirmationDraftActionChange(null);
		};
	}, [
		existingDraft,
		handleSaveDraft,
		isLoading,
		isSavingDraft,
		mode,
		onExitConfirmationDraftActionChange,
	]);

	const handleSubmitForm = handleSubmit(
		(formData: BulkConsignmentOrderFormData) => {
			if (isEditMode) {
				// EDIT MODE - Update existing bulk orders (only quantities and comments)
				const updatePayload = {
					ta_comments: formData.comments || '',
					products:
						formData.products?.map((product: any) => ({
							orderId: product.orderId!,
							ta_quantity: String(product.quantity || 1),
						})) || [],
				};

				setActiveMutation('update');
				updateBulkOrder({
					moduleName: BgProcessModule.CONSIGNMENT_ORDER,
					userId: userId || '',
					payload: {
						payload: updatePayload,
						additionalPayload: {
							clinicName: (watch('clinic') as SelectItem)?.label as string,
							physicianName: (watch('physician') as SelectItem)?.label as string,
							consignmentTypeName: (watch('consignmentType') as SelectItem)?.label as string,
						} 
					},
					clinicId: (watch('clinic') as SelectItem)?.value as string,
				});
			} else {
				// CREATE MODE or REORDER MODE
				if (formData.products && formData.products.length > 0) {
					const bulkOrderPayload = transformBulkFormToBulkConsignmentOrderRequest(formData as any);
					setActiveMutation('create');
					createBulkOrder({
						moduleName: BgProcessModule.CONSIGNMENT_ORDER,
						userId: userId || '',
						payload: {
							payload: bulkOrderPayload,
							additionalPayload: {
								clinicName: (watch('clinic') as SelectItem)?.label as string,
								physicianName: (watch('physician') as SelectItem)?.label as string,
								consignmentTypeName: (watch('consignmentType') as SelectItem)?.label as string,
							} 
						},
						clinicId: (watch('clinic') as SelectItem)?.value as string,
					});
				}
			}
		},
	);

	const handleAddProduct = () => {
		append({
			product: null as any,
			productSize: '',
			quantity: 1,
		});
	};

	const handleRemoveProduct = (index: number) => {
		if (fields.length > 1) {
			remove(index);
		}
	};

	// Watch all form values for validation
	const formValues = watch();

	// Check if all required fields are filled for submit button validation
	const isFormValid = useMemo(() => {
		const hasClinic = !!formValues.clinic?.value;
		const hasConsignmentType = !!formValues.consignmentType?.value;
		const hasClinicPO =
			!!formValues.clinicPO && formValues.clinicPO.trim() !== '';
		const hasShipping = !!formValues.shipping?.value;
		const hasShippingLocation = !!formValues.shippingLocation?.value;
		const hasStatus = !!formValues.status && formValues.status.trim() !== '';

		const hasProducts = formValues.products && formValues.products.length > 0;

		const allProductsValid =
			hasProducts &&
			formValues.products!.every((product: any) => {
				return (
					!!product.product?.value &&
					!!product.productSize &&
					product.productSize.trim() !== '' &&
					product.quantity &&
					Number(product.quantity) > 0
				);
			});

		return (
			hasClinic &&
			hasConsignmentType &&
			hasClinicPO &&
			hasShipping &&
			hasShippingLocation &&
			hasStatus &&
			allProductsValid
		);
	}, [formValues]);

	return (
		<form onSubmit={handleSubmitForm}>
			<Box
				display="flex"
				justifyContent="center"
				px={{ base: 4, md: 6, lg: 2 }}
				py={{ base: 2, md: 2, lg: 1 }}
				pb={{ base: 2, md: 2, lg: 2 }}
			>
				<Grid
					templateColumns={{ base: '1fr', md: 'repeat(2, 1fr)' }}
					gap={{ base: 3, md: 4, lg: 3 }}
					columnGap={{ base: 0, md: 4 }}
					w="100%"
				>
					{/* Consignment Type and Clinic Row */}
					<GridItem colSpan={{ base: 1, md: 1 }}>
						<FaceliftSelect<SelectItem, false>
							topLabel="Consignment Type"
							placeholder="Select Type"
							value={watch('consignmentType')}
							onChange={(newValue: SingleValue<SelectItem>) => {
								if (newValue) {
									setValue('consignmentType', newValue, { shouldDirty: true });
								}
							}}
							items={consignmentTypeItems}
							error={errors.consignmentType?.message as unknown as string}
							isSearchable={false}
							isDisabled={shouldLockFields}
							required
						/>
					</GridItem>
					<GridItem colSpan={{ base: 1, md: 1 }}>
						<FaceliftSelect<SelectItem, false>
							topLabel="Clinic"
							placeholder="Select Clinic"
							value={watch('clinic')}
							onChange={(newValue: SingleValue<SelectItem>) => {
								if (newValue) {
									setValue('clinic', newValue, { shouldDirty: true });
									setValue('physician', null, {
										shouldDirty: true,
									});
									setValue('shippingLocation', null as any, {
										shouldDirty: true,
									});
								}
							}}
							items={shouldLockFields ? [] : clinicItems}
							error={errors.clinic?.message as unknown as string}
							isSearchable={true}
							isDisabled={shouldLockFields}
							required
						/>
					</GridItem>

					{/* Provider and Clinic PO Row */}
					<GridItem colSpan={{ base: 1, md: 1 }}>
						<FaceliftSelect<SelectItem, false>
							limit={15}
							topLabel="Provider"
							placeholder="Select Provider (Optional)"
							value={watch('physician')}
							onChange={(newValue: SingleValue<SelectItem>) => {
								if (newValue) {
									setValue('physician', newValue, { shouldDirty: true });
								}
							}}
							items={shouldLockFields ? [] : physicians.items}
							loading={physicians.loading}
							onLoadMore={physicians.onLoadMore}
							isDisabled={shouldLockFields || !watch('clinic')}
							error={errors.physician?.message as unknown as string}
							isSearchable={true}
						/>
					</GridItem>

					{/* Clinic PO and Shipping Method Row */}
					<GridItem colSpan={{ base: 1, md: 1 }}>
						<FaceliftInput
							topLabel="Clinic PO"
							placeholder="Enter Clinic PO"
							size="sm"
							{...register('clinicPO')}
							error={errors.clinicPO?.message}
							disabled={shouldLockFields}
							required
						/>
					</GridItem>
					<GridItem colSpan={{ base: 1, md: 1 }}>
						<FaceliftSelect<SelectItem, false>
							topLabel="Shipping Method"
							placeholder="Select"
							value={watch('shipping')}
							onChange={(newValue: SingleValue<SelectItem>) => {
								if (newValue) {
									setValue('shipping', newValue, { shouldDirty: true });
								}
							}}
							items={shippingMethodItems}
							error={errors.shipping?.message as unknown as string}
							isSearchable={false}
							isDisabled={shouldLockFields}
							required
						/>
					</GridItem>

					{/* Status and Shipping Location Row */}
					<GridItem colSpan={{ base: 1, md: 1 }}>
						<FaceliftSelect<SelectItem, false>
							topLabel="Status"
							placeholder="Select Status"
							value={
								statusOptions.find((s) => s.value === watch('status')) || null
							}
							onChange={(newValue: SingleValue<SelectItem>) => {
								if (newValue) {
									setValue('status', String(newValue.value), { shouldDirty: true });
								}
							}}
							items={statusOptions}
							error={errors.status?.message as unknown as string}
							isSearchable={false}
							isDisabled={shouldLockFields}
							required
						/>
					</GridItem>
					<GridItem colSpan={{ base: 1, md: 1 }}>
						<FaceliftSelect<SelectItem, false>
							limit={15}
							topLabel="Shipping Location"
							placeholder="Select Location"
							value={watch('shippingLocation')}
							onChange={(newValue: SingleValue<SelectItem>) => {
								if (newValue) {
									setValue('shippingLocation', newValue, { shouldDirty: true });
								}
							}}
							items={shouldLockFields ? [] : shippingLocations.items}
							loading={shippingLocations.loading}
							onLoadMore={shippingLocations.onLoadMore}
							isDisabled={shouldLockFields || !watch('clinic')}
							error={errors.shippingLocation?.message as unknown as string}
							isSearchable={true}
							required
						/>
					</GridItem>

					{/* Products Section - Full Width */}
					<GridItem colSpan={{ base: 1, md: 2 }}>
						<Box pt={4} mt={2}>
							{fields.map((field, index) => (
								<Box key={field.id} mb={3}>
									<Grid
										templateColumns={{
											base: '1fr',
											md: '1fr 1fr 1fr 45px',
										}}
										gap={3}
										alignItems="end"
									>
										<GridItem>
											<FaceliftSelect<SelectItem, false>
												topLabel={index === 0 ? 'Product Selection' : ''}
												placeholder="Select Product"
												value={watch(`products.${index}.product`)}
												onChange={(newValue: SingleValue<SelectItem>) => {
													if (newValue) {
														setValue(`products.${index}.product`, newValue, { shouldDirty: true });
														setValue(`products.${index}.productSize`, '', { shouldDirty: true });
													}
												}}
												items={shouldLockFields ? [] : getFilteredProducts(index)}
												loading={productsLoading}
												isDisabled={shouldLockFields || !watch('clinic')}
												error={
													errors.products?.[index]?.product
														?.message as unknown as string
												}
												isSearchable={true}
												required
											/>
										</GridItem>
										<GridItem>
											<FaceliftSelect<SelectItem, false>
												topLabel={index === 0 ? 'Size' : ''}
												placeholder="Select Size"
												value={
													shouldLockFields &&
														watch(`products.${index}.productSize`)
														? ({
															value: watch(`products.${index}.productSize`),
															label:
																watch(`products.${index}.productSizeLabel`) ||
																watch(`products.${index}.productSize`),
														} as SelectItem)
														: watch(`products.${index}.product`)
															? getProductSizes(
																(watch(`products.${index}.product`) as any)
																	?.value,
																index,
															).find(
																(s) =>
																	s.value ===
																	watch(`products.${index}.productSize`),
															) || null
															: null
												}
												onChange={(newValue: SingleValue<SelectItem>) => {
													if (newValue) {
														setValue(
															`products.${index}.productSize`,
															String(newValue.value),
															{ shouldDirty: true }
														);
														setValue(
															`products.${index}.productSizeLabel`,
															String(newValue.label),
															{ shouldDirty: true }
														);
													}
												}}
												items={
													shouldLockFields
														? []
														: watch(`products.${index}.product`)
															? getProductSizes(
																(watch(`products.${index}.product`) as any)
																	?.value,
																index,
															)
															: []
												}
												isDisabled={
													shouldLockFields ||
													!watch(`products.${index}.product`)
												}
												error={errors.products?.[index]?.productSize?.message}
												isSearchable={true}
												required
											/>
										</GridItem>
										<GridItem>
											<Controller
												control={control}
												name={`products.${index}.quantity`}
												render={({ field }) => {
													const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
														const raw = e.target.value;
														if (raw === '') {
															field.onChange('');
															return;
														}
														const n = parseInt(raw, 10);
														if (!Number.isNaN(n) && n < 1) {
															field.onChange(1);
															return;
														}
														field.onChange(raw === '' ? '' : Number.isNaN(n) ? raw : n);
													};
													return (
														<FaceliftInput
															topLabel={index === 0 ? 'Quantity' : ''}
															placeholder="-"
															type="number"
															size="sm"
															min={1}
															value={field.value ?? ''}
															onChange={handleChange}
															onBlur={field.onBlur}
															error={errors.products?.[index]?.quantity?.message}
															disabled={false}
															required
														/>
													);
												}}
											/>
										</GridItem>
										<GridItem>
											<Show when={index > 0 && mode === 'create'}>
												<Box
													display="flex"
													alignItems="flex-end"
													justifyContent="center"
													h="40px"
												>
													<FaceliftButton
														type="button"
														onClick={() => handleRemoveProduct(index)}
														bg="transparent"
														color="red.500"
														border="1px solid"
														borderColor="red.500"
														minW="40px"
														w="40px"
														h="40px"
														p={0}
														_hover={{ bg: 'red.50' }}
														_dark={{
															_hover: {
																bg: 'whiteAlpha.100',
															},
														}}
													>
														<RiDeleteBin6Line size={12} />
													</FaceliftButton>
												</Box>
											</Show>
										</GridItem>
									</Grid>
								</Box>
							))}
							<Show when={mode === 'create'}>
								<Box mt={2}>
									<FaceliftButton
										type="button"
										onClick={handleAddProduct}
										bg="transparent"
										color="brand.600"
										border="1px solid"
										borderColor="brand.600"
										minW="140px"
										_hover={{
											bg: 'brand.50',
										}}
										_dark={{
											_hover: {
												bg: 'whiteAlpha.100',
											},
										}}
										startIcon={<RiAddLine size={20} />}
									>
										Add Product
									</FaceliftButton>
								</Box>
							</Show>
						</Box>
					</GridItem>

					{/* Comments - Full Width */}
					<GridItem colSpan={{ base: 1, md: 2 }}>
						<FaceliftInput
							topLabel="Comments/Clarifications"
							placeholder="Type Here"
							size="sm"
							{...register('comments')}
							error={errors.comments?.message}
							disabled={false}
						/>
					</GridItem>

					{/* Buttons - Full Width */}
					<GridItem colSpan={{ base: 1, md: 2 }}>
						<Box
							display="flex"
							flexDirection={{ base: 'column', sm: 'row' }}
							gap={3}
							justifyContent={{ base: 'center', lg: 'flex-end' }}
							mt={{ base: 0, lg: 1 }}
						>
							<FaceliftButton
								minW={{ base: '100%', sm: '160px' }}
								loading={isLoading}
								type="submit"
								cursor="pointer"
								disabled={!isFormValid || isLoading || isSavingDraft}
							>
								{isEditMode ? 'Update' : 'Submit'}
							</FaceliftButton>
							
							<Show when={mode === 'create' && !existingDraft}>
								<FaceliftButton
									minW={{ base: '100%', sm: '160px' }}
									bg="transparent"
									color="inherit"
									border="1px solid"
									onClick={handleSaveDraft}
									loading={isSavingDraft}
									disabled={isLoading || isSavingDraft}
									type="button"
								>
									Save as Draft
								</FaceliftButton>
							</Show>
						</Box>
					</GridItem>
				</Grid>
			</Box>

			<FaceliftSuccessProgressModal
				open={modalOpen}
				globalLoading={isLoading}
				isLoading={isLoading}
				isSucceed={isMutationSuccess}
				hasError={hasError}
				errorMessage={errorMessage}
				onOpenChange={(newOpen: boolean) => {
					if (!newOpen) setActiveMutation(null);
				}}
				title={successMessage || fallbackTitle}
			/>
		</form>
	);
};

export default BulkConsignmentOrderForm;
